import type { LegacyCheckoutConfig } from "../../../Common/legacy-checkout-config/read-legacy-checkout-config";
import type { getLastKnownAddress } from "../../delivery-details/address-sync-cache";
import { isCartItemUnavailable } from "../../model/mybag";

export const CART_ITEM_UNAVAILABLE_MESSAGE =
    "Some items in your bag are no longer available in the requested quantity. Please review your bag and try again.";

export function assertCartItemsAvailable(cart: unknown): void {
    if (cart === null || typeof cart !== "object") {
        return;
    }
    const { items } = cart as { items?: unknown };
    if (!Array.isArray(items)) {
        return;
    }

    const hasUnavailableItem = items.some((item: unknown) => {
        if (item === null || typeof item !== "object" || Array.isArray(item)) {
            return false;
        }
        if (!("bbnt_stock_status" in item) && !("errors" in item)) {
            return false;
        }
        const cartItem = item as {
            bbnt_stock_status?: string | null;
            errors?: unknown;
        };
        if (
            cartItem.bbnt_stock_status != null &&
            typeof cartItem.bbnt_stock_status !== "string"
        ) {
            return false;
        }
        if (cartItem.errors != null && !Array.isArray(cartItem.errors)) {
            return false;
        }
        return isCartItemUnavailable(cartItem);
    });
    if (hasUnavailableItem) {
        throw new Error(CART_ITEM_UNAVAILABLE_MESSAGE);
    }
}

export const MISSING_CHECKOUT_CONFIG_MESSAGE =
    "Checkout configuration is unavailable. Please refresh the page and try again.";

export interface KickoffDeps {
    setIsLoading: (value: boolean) => void;
    enablePooling: () => void;
    flushPooling: () => void;
    snapshotForm: () => void;
    getLegacyCheckoutConfig: () => unknown;
    getLastKnownAddress: typeof getLastKnownAddress;
    updateLegacyCheckoutConfig: () => Promise<void>;
    navigateToPayment: () => void;
    setHasBeenToPaymentPage: (value: boolean) => void;
    kickoffInternals: () => Promise<void>;
    setProminentError: (message: string) => void;
    navigateToDelivery: () => void;
    clearSnapshot: () => void;
}

export function prepareLegacyCheckoutConfigForKickoff(
    config: unknown,
    lastKnownAddress: ReturnType<typeof getLastKnownAddress>,
): LegacyCheckoutConfig {
    if (config === null || typeof config !== "object") {
        throw new Error(MISSING_CHECKOUT_CONFIG_MESSAGE);
    }
    const checkoutConfig = config as LegacyCheckoutConfig & {
        selectedShippingMethod?: unknown;
    };
    if (checkoutConfig.selectedShippingMethod == null) {
        checkoutConfig.selectedShippingMethod =
            Object.keys(lastKnownAddress).length > 0 ? lastKnownAddress : {};
    }
    return checkoutConfig;
}

export async function runPaymentPageKickoff(deps: KickoffDeps): Promise<void> {
    try {
        try {
            deps.setIsLoading(true);
            deps.enablePooling();
            deps.snapshotForm();
            prepareLegacyCheckoutConfigForKickoff(
                deps.getLegacyCheckoutConfig(),
                deps.getLastKnownAddress("shipping"),
            );

            // The legacy customer must be current before payment builds its REST URL.
            await deps.updateLegacyCheckoutConfig();
            deps.navigateToPayment();
            deps.setHasBeenToPaymentPage(true);
            await deps.kickoffInternals();
        } finally {
            // Attempt each cleanup once, even if an earlier cleanup operation throws.
            try {
                deps.flushPooling();
            } finally {
                try {
                    deps.setIsLoading(false);
                } finally {
                    deps.clearSnapshot();
                }
            }
        }
    } catch (error) {
        console.error(error);
        const errorMessage =
            error instanceof Error && error.message
                ? error.message
                : "An unknown error has occurred.";
        deps.setProminentError(errorMessage);
        deps.setHasBeenToPaymentPage(false);
        deps.navigateToDelivery();
    }
}
